/*
 * The Catalogue screen (CAE-281, docs/reference — the Catalogue browse page): browse the
 * live-synced MLB catalogue scoped by the allowlists. First real, data-backed screen in the
 * shell — every other nav section is still the generic placeholder.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CountBadge,
  EmptyState,
  Input,
  SearchInput,
  Select,
  toast,
} from '@cou/admin-ui';
import { useAdminAuth } from '../session/admin-auth-context';
import { AdminShell } from '../shell/AdminShell';
import {
  getSyncRuns,
  listEvents,
  listMarkets,
  syncCatalogue,
  syncMetadata,
  type CatalogueEvent,
  type CatalogueMarket,
  type CatalogueResult,
  type SyncRun,
} from '../catalogue/catalogue-client';
import {
  eventDateKey,
  formatRelativeSync,
  groupMarketsByType,
  syncCardView,
  type SyncCardTone,
} from '../catalogue/format';
import { CatalogueEventRow } from './CatalogueEventRow';

type FeedStatus = { kind: 'loading' } | { kind: 'connected' } | { kind: 'disconnected' };

/** The latest run per kind (CAE-469), server-fed so status survives reload and reflects nightly runs. */
type SyncRuns = { catalogue: SyncRun | null; metadata: SyncRun | null };

/** Status-card token classes per tone — success-green only ever for a recorded `succeeded` run. */
const TONE_CLASSES: Record<SyncCardTone, { wrap: string; dot: string; text: string }> = {
  idle: {
    wrap: 'border-state-draft-border bg-state-draft-bg',
    dot: 'bg-state-draft-dot',
    text: 'text-state-draft-text',
  },
  running: {
    wrap: 'border-state-scheduled-border bg-state-scheduled-bg',
    dot: 'bg-state-scheduled-dot animate-pulse-dot',
    text: 'text-state-scheduled-text',
  },
  success: {
    wrap: 'border-state-open-border bg-state-open-bg',
    dot: 'bg-state-open-dot',
    text: 'text-state-open-text',
  },
  failure: {
    wrap: 'border-state-void-border bg-state-void-bg',
    dot: 'bg-state-void-dot',
    text: 'text-state-void-text',
  },
};

/** One server-fed sync status card (CAE-469): last-sync time + outcome for a sync. */
function SyncStatusCard({ noun, run }: { noun: string; run: SyncRun | null }) {
  const view = syncCardView(noun, run);
  const tone = TONE_CLASSES[view.tone];
  return (
    <div
      className={`flex items-center gap-2.5 rounded-row border-[0.5px] px-3 py-2.5 ${tone.wrap}`}
      data-testid={`sync-card-${noun.toLowerCase()}`}
      data-tone={view.tone}
    >
      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${tone.dot}`} />
      <span className="flex min-w-0 flex-col gap-px">
        <span className={`font-ui text-[12.5px] font-semibold ${tone.text}`}>{view.title}</span>
        <span className="font-mono text-[11px] text-ink-4">
          {formatRelativeSync(view.timestamp)}
        </span>
      </span>
    </div>
  );
}

const SPORT_OPTIONS = [
  { value: 'all', label: 'All sports' },
  { value: 'baseball', label: 'Baseball' },
];

const MARKET_STATE_OPTIONS = [
  { value: 'all', label: 'All states' },
  { value: 'selectable', label: 'Selectable' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'out_of_bounds', label: 'Out of bounds' },
  { value: 'started', label: 'Started' },
];

const VERDICTS = ['selectable', 'suspended', 'out_of_bounds', 'started'] as const;
type Verdict = (typeof VERDICTS)[number];

/** Narrows a filter string to a real verdict key, so `event.verdictCounts` reads without a cast. */
function isVerdict(value: string): value is Verdict {
  return (VERDICTS as readonly string[]).includes(value);
}

function toolbarSubtext(filteredCount: number, totalCount: number): string {
  if (filteredCount === totalCount) {
    return `Showing all ${totalCount} synced event${totalCount === 1 ? '' : 's'}`;
  }
  return `Showing ${filteredCount} of ${totalCount} synced events`;
}

export function CatalogueScreen() {
  const { claims, getToken } = useAdminAuth();
  const canSync = claims?.permissions.includes('catalogue:sync') ?? false;

  const [feedStatus, setFeedStatus] = useState<FeedStatus>({ kind: 'loading' });
  const [events, setEvents] = useState<readonly CatalogueEvent[]>([]);
  // Populated lazily, only for an Event whose row has been expanded (CAE-322) — the closed-row
  // summary and the market-state filter run off each Event's own counts instead.
  const [marketsByEvent, setMarketsByEvent] = useState<Record<string, readonly CatalogueMarket[]>>(
    {},
  );
  const [loadingMarketsEventId, setLoadingMarketsEventId] = useState<string | null>(null);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  // The latest recorded run per kind (CAE-469), fed to the status cards. Null until first loaded.
  const [syncRuns, setSyncRuns] = useState<SyncRuns | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [competitionFilter, setCompetitionFilter] = useState('all');
  const [sportFilter, setSportFilter] = useState('all');
  const [marketStateFilter, setMarketStateFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');

  // Guards against a fast filter change being clobbered by a slower, now-stale response: only the
  // most recently issued request is allowed to commit its result.
  const requestId = useRef(0);

  // Fetches an Event's Markets once, on demand (CAE-322) — never eagerly for the whole list, so
  // browsing a catalogue no longer issues one markets request per Event.
  const ensureMarketsLoaded = useCallback(
    (eventId: string) => {
      const token = getToken();
      if (
        token === null ||
        marketsByEvent[eventId] !== undefined ||
        loadingMarketsEventId === eventId
      ) {
        return;
      }
      // Tied to the same generation counter as loadAll, so a reload that fires mid-fetch (sync,
      // sport change) invalidates this response instead of writing stale Markets into the
      // just-cleared cache (CAE-322).
      const id = requestId.current;
      setLoadingMarketsEventId(eventId);
      void listMarkets(token, eventId).then((result) => {
        if (id !== requestId.current) return;
        setLoadingMarketsEventId((prev) => (prev === eventId ? null : prev));
        if (!result.ok) return;
        setMarketsByEvent((prev) => ({ ...prev, [eventId]: result.markets }));
      });
    },
    [getToken, marketsByEvent, loadingMarketsEventId],
  );

  const loadAll = useCallback(
    (sport: string = sportFilter) => {
      const token = getToken();
      if (token === null) return;
      const id = ++requestId.current;
      setFeedStatus({ kind: 'loading' });
      // The recorded sync runs (CAE-469): last-sync time + outcome for both syncs, server-fed so
      // the cards survive a reload and reflect nightly runs. Reloaded whenever the events are.
      void getSyncRuns(token).then((result) => {
        if (id !== requestId.current || !result.ok) return;
        setSyncRuns({ catalogue: result.catalogue, metadata: result.metadata });
      });
      void listEvents(token, sport === 'all' ? undefined : sport).then((result) => {
        if (id !== requestId.current) return;
        if (!result.ok) {
          setFeedStatus({ kind: 'disconnected' });
          return;
        }
        setEvents(result.events);
        // The cache goes stale on every reload (sync or filter change) — collapse any expanded
        // row and drop any in-flight fetch's target rather than leave one showing (or about to
        // show) now-superseded Markets (CAE-322).
        setMarketsByEvent({});
        setExpandedEventId(null);
        setLoadingMarketsEventId(null);
        setFeedStatus({ kind: 'connected' });
      });
    },
    [getToken, sportFilter],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  function toggleExpanded(eventId: string) {
    const willExpand = expandedEventId !== eventId;
    setExpandedEventId(willExpand ? eventId : null);
    if (willExpand) ensureMarketsLoaded(eventId);
  }

  /**
   * Run a sync trigger (API-08/API-18) and toast its outcome. The status cards are server-fed
   * (CAE-469): `loadAll` refetches the recorded runs, so a success or failure surfaces from the
   * server rather than from React-only state that a reload would lose.
   */
  function runSync(
    sync: (token: string) => Promise<CatalogueResult<{ durationMs: number }>>,
    successMessage: string,
  ) {
    const token = getToken();
    if (token === null) return;
    void sync(token).then((result) => {
      if (result.ok) {
        toast.success(successMessage);
      } else {
        toast.error(result.message ?? 'Sync failed');
      }
      // Reload either way: a failed run is now recorded server-side and must surface on the card.
      loadAll();
    });
  }

  // "Sync events" and "Sync markets" both call API-08 — the single sync endpoint pulls Events,
  // Markets, and Outcomes together (no eventIds-scoped variant exists) — kept as two buttons
  // to match the design and to name the outcome the operator most cares about triggering.
  const syncActions = canSync ? (
    <>
      <Button variant="outline" onClick={() => runSync(syncCatalogue, 'Events sync complete')}>
        Sync events
      </Button>
      <Button variant="primary" onClick={() => runSync(syncCatalogue, 'Markets sync complete')}>
        Sync markets
      </Button>
      <Button variant="outline" onClick={() => runSync(syncMetadata, 'Metadata sync complete')}>
        Sync metadata
      </Button>
    </>
  ) : null;

  if (feedStatus.kind === 'disconnected') {
    return (
      <AdminShell
        title="Catalogue"
        subtitle="Browse synced events, market types, and outcomes from the feed."
        actions={syncActions}
      >
        <EmptyState
          variant="page"
          title="Catalogue feed unavailable"
          body="We couldn't reach the Live catalogue. Check the connection and try again."
          action={
            <Button variant="primary" onClick={() => loadAll()}>
              Retry
            </Button>
          }
        />
      </AdminShell>
    );
  }

  const competitionOptions = [
    { value: 'all', label: 'All competitions' },
    ...[
      ...new Set(events.map((event) => event.competition).filter((c): c is string => c !== null)),
    ].map((competition) => ({ value: competition, label: competition })),
  ];

  // The Event's own verdict tally (CAE-322) — no per-event markets fetch needed to filter or to
  // show a filtered prop count in the closed row.
  const activeVerdictFilter = isVerdict(marketStateFilter) ? marketStateFilter : null;

  const filteredEvents = events.filter((event) => {
    if (searchQuery && !event.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (competitionFilter !== 'all' && event.competition !== competitionFilter) return false;
    if (dateFilter && eventDateKey(event.startTime) !== dateFilter) return false;
    if (activeVerdictFilter && event.verdictCounts[activeVerdictFilter] === 0) return false;
    return true;
  });

  return (
    <AdminShell
      title="Catalogue"
      subtitle="Browse synced events, market types, and outcomes from the feed."
      actions={syncActions}
    >
      <div className="grid grid-cols-[244px_minmax(0,1fr)] items-start gap-5">
        <aside className="flex flex-col gap-3.5">
          {/* Both cards are server-fed (CAE-469): last-sync time + outcome for the catalogue and
              metadata syncs, surviving reload and reflecting nightly runs. Absent until first load,
              matching the events list. */}
          {syncRuns !== null ? (
            <>
              <SyncStatusCard noun="Catalogue" run={syncRuns.catalogue} />
              <SyncStatusCard noun="Metadata" run={syncRuns.metadata} />
            </>
          ) : null}
          <Card eyebrow="Filters">
            <div className="flex flex-col gap-3">
              <SearchInput
                pill={false}
                placeholder="Search events"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Select
                label="Competition"
                options={competitionOptions}
                value={competitionFilter}
                onChange={setCompetitionFilter}
              />
              <Select
                label="Sport"
                options={SPORT_OPTIONS}
                value={sportFilter}
                onChange={setSportFilter}
              />
              <Select
                label="Market state"
                options={MARKET_STATE_OPTIONS}
                value={marketStateFilter}
                onChange={setMarketStateFilter}
              />
              <Input
                label="Date"
                type="date"
                mono
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              />
            </div>
          </Card>
        </aside>
        <main className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="font-ui text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-4">
                Events
              </span>
              <CountBadge count={filteredEvents.length} />
            </div>
            <span className="font-ui text-[12.5px] text-ink-3">
              {toolbarSubtext(filteredEvents.length, events.length)}
            </span>
          </div>
          {filteredEvents.length === 0 ? (
            <EmptyState
              variant="filtered"
              title="No events match"
              body="No synced events match your filters."
            />
          ) : (
            <div className="flex flex-col gap-2">
              {filteredEvents.map((event) => {
                const markets = marketsByEvent[event.id] ?? [];
                const visibleMarkets = activeVerdictFilter
                  ? markets.filter((m) => m.verdict === activeVerdictFilter)
                  : markets;
                return (
                  <CatalogueEventRow
                    key={event.id}
                    event={event}
                    groups={groupMarketsByType(visibleMarkets)}
                    expanded={expandedEventId === event.id}
                    loading={loadingMarketsEventId === event.id}
                    // The row's own total, unless a verdict filter narrows what's shown — then
                    // the matching subset, from the same tally the filter itself used, so the
                    // closed-row summary never disagrees with what expanding it reveals (CAE-322).
                    filteredPropCount={
                      activeVerdictFilter ? event.verdictCounts[activeVerdictFilter] : null
                    }
                    onToggleExpanded={toggleExpanded}
                  />
                );
              })}
            </div>
          )}
        </main>
      </div>
    </AdminShell>
  );
}
